// src/slices/ticketRecall/reducer.ts
// Redux slice cho phân hệ Thu hồi vé ế (mục 13.3, REQUIRE_FIXBUGS.md)

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TicketRecall } from 'pages/TicketRecall/types';
import { TicketRecallFilters, TicketRecallModalState, TicketRecallState } from './types';
import { fetchTicketRecalls, createTicketRecall, confirmTicketRecall, cancelTicketRecall } from './thunk';

// README.md mục 7 — mặc định hiển thị ĐẦY ĐỦ phiếu ở mọi trạng thái (không chỉ Draft) để xem được
// toàn bộ tiến trình xử lý; thao tác Xác nhận/Huỷ vẫn chỉ khả dụng với phiếu Draft (state machine
// chỉ cho Draft → Confirmed/Cancelled — xem isDraft trong components/TicketRecallList.tsx), các
// trạng thái khác chỉ xem được (view-only). Người dùng vẫn tự đổi filter để lọc theo trạng thái.
const initialFilters: TicketRecallFilters = {
  planId: '',
  agentId: '',
  status: 'All',
};

const initialState: TicketRecallState = {
  list: [],
  selectedItem: null,
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 0,
  },
  filters: { ...initialFilters },
  loading: false,
  submitting: false,
  error: null,
  modalState: {
    create: false,
    confirm: false,
    cancel: false,
    detail: false,
  },
};

const ticketRecallSlice = createSlice({
  name: 'TicketRecall',
  initialState,
  reducers: {
    setSelectedItem: (state, action: PayloadAction<TicketRecall | null>) => {
      state.selectedItem = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<TicketRecallFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = { ...initialFilters };
      state.pagination.currentPage = 1;
    },
    clearError: (state) => {
      state.error = null;
    },
    openModal: (state, action: PayloadAction<keyof TicketRecallModalState>) => {
      state.modalState[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof TicketRecallModalState>) => {
      state.modalState[action.payload] = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTicketRecalls.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTicketRecalls.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.list = payload?.items || payload?.data || payload || [];
        state.pagination.totalItems = payload?.totalItems ?? payload?.totalCount ?? state.list.length;
        state.pagination.totalPages =
          payload?.totalPages ??
          Math.ceil(state.pagination.totalItems / Math.max(state.pagination.pageSize, 1));
      })
      .addCase(fetchTicketRecalls.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(createTicketRecall.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(createTicketRecall.fulfilled, (state) => {
        state.submitting = false;
        state.modalState.create = false;
      })
      .addCase(createTicketRecall.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Backend Confirm/Cancel chỉ trả về id (long), không phải TicketRecallDto đầy đủ — danh
      // sách được nạp lại bằng fetchTicketRecalls ngay sau khi thành công (xem index.tsx), nên ở
      // đây chỉ cần đóng modal + tắt submitting, không patch state.list tại chỗ.
      .addCase(confirmTicketRecall.pending, (state) => {
        state.submitting = true;
      })
      .addCase(confirmTicketRecall.fulfilled, (state) => {
        state.submitting = false;
        state.modalState.confirm = false;
        state.selectedItem = null;
      })
      .addCase(confirmTicketRecall.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      .addCase(cancelTicketRecall.pending, (state) => {
        state.submitting = true;
      })
      .addCase(cancelTicketRecall.fulfilled, (state) => {
        state.submitting = false;
        state.modalState.cancel = false;
        state.selectedItem = null;
      })
      .addCase(cancelTicketRecall.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedItem,
  setPage,
  setFilters,
  clearFilters,
  clearError,
  openModal,
  closeModal,
} = ticketRecallSlice.actions;

export default ticketRecallSlice.reducer;
